import { useEffect, useState } from "react";
import { api, type EntityData } from "../api.js";
import type { JSX } from "react";

const LEI_RE = /^[A-Z0-9]{18}[0-9]{2}$/;

interface Props {
  registerId: string;
  onSaved: () => void;
}

export function EntityForm({ registerId, onSaved }: Props): JSX.Element {
  const [lei, setLei] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [refDate, setRefDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api
      .getEntity(registerId)
      .then(({ entity }) => {
        if (entity !== null) {
          setLei(entity.lei);
          setName(entity.name);
          setCountry(entity.country);
          setRefDate(entity.refDate);
          setSaved(true);
        }
      })
      .catch(() => {
        // entity load failed - leave form empty
      });
  }, [registerId]);

  function validate(): string | null {
    if (!LEI_RE.test(lei.toUpperCase())) {
      return "LEI must be 18 uppercase alphanumeric chars followed by 2 digits (20 chars total).";
    }
    if (country.length !== 2) {
      return "Country must be exactly 2 letters (ISO 3166-1 alpha-2).";
    }
    if (!name.trim()) {
      return "Entity name is required.";
    }
    if (!refDate) {
      return "Reference date is required.";
    }
    return null;
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const msg = validate();
    if (msg !== null) {
      setError(msg);
      return;
    }
    const entity: EntityData = {
      lei: lei.toUpperCase(),
      name: name.trim(),
      country: country.toUpperCase(),
      refDate,
    };
    setBusy(true);
    setError(null);
    api
      .saveEntity(registerId, entity)
      .then(() => {
        setSaved(true);
        onSaved();
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setBusy(false));
  }

  return (
    <form className="entity-form" data-testid="entity-form" onSubmit={handleSubmit}>
      <h3 style={{ marginTop: 0 }}>
        Legal Entity {saved && <span className="badge badge-ready">saved</span>}
      </h3>
      {error !== null && <div className="error">{error}</div>}
      <label>
        LEI (20 chars)
        <input
          data-testid="entity-lei"
          type="text"
          value={lei}
          onChange={(e) => setLei(e.target.value)}
          placeholder="e.g. 5493001KJTIIGC8Y1R17"
          maxLength={20}
        />
      </label>
      <label>
        Entity Name
        <input
          data-testid="entity-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Acme Bank SA"
        />
      </label>
      <label>
        Country (2-letter ISO)
        <input
          data-testid="entity-country"
          type="text"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="e.g. NL"
          maxLength={2}
        />
      </label>
      <label>
        Reference Date
        <input
          data-testid="entity-refdate"
          type="date"
          value={refDate}
          onChange={(e) => setRefDate(e.target.value)}
        />
      </label>
      <button type="submit" data-testid="entity-submit" disabled={busy}>
        {busy ? "Saving..." : "Save entity"}
      </button>
    </form>
  );
}
